import { addToast, Icon, Table } from '@octano/global-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTexts } from '../hooks/useTexts';
import { useLayoutState } from '../../../../../hooks/useLayoutState';
import { getStudentDocumentDownload } from '../../../api/studentDocumentsRequests';
import { StudentDocument } from '../types';
import { DocumentPreviewModal } from './DocumentPreviewModal';

type DocumentsOthersTableProps = {
  studentId: number;
  data: StudentDocument[];
  onDelete: (document: StudentDocument) => void | Promise<void>;
  hasPermissionToDelete?: boolean;
};

export function DocumentsOthersTable({
  studentId,
  data,
  onDelete,
  hasPermissionToDelete,
}: DocumentsOthersTableProps) {
  const texts = useTexts();
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<{
    isOpen: boolean;
    url: string | null;
    title?: string;
  }>({ isOpen: false, url: null });
  const { showErrorModal } = useLayoutState();
  const { t } = useTranslation();

  const getUrl = async (studentDocumentId: number) => {
    setLoading(true);
    const result = await getStudentDocumentDownload({
      studentId,
      studentDocumentId,
    });
    const { error, data } = result;
    setLoading(false);
    if (error && error.code === 'CONNECTION') {
      showErrorModal();
    } else if (error) {
      addToast({
        icon: 'error',
        color: 'danger',
        text: t('common.errors.get'),
      });
    }
    if (data) {
      window.open(data.url, '_blank');
    }
  };

  const viewDocument = async (document: StudentDocument) => {
    setLoading(true);
    const result = await getStudentDocumentDownload({
      studentId,
      studentDocumentId: document.id,
    });
    const { error, data } = result;
    setLoading(false);
    if (error && error.code === 'CONNECTION') {
      showErrorModal();
    } else if (error) {
      addToast({
        icon: 'error',
        color: 'danger',
        text: t('common.errors.get'),
      });
    }
    if (data) {
      setPreview({ isOpen: true, url: data.url, title: document.name });
    }
  };

  return (
    <>
      <Table
        data={data}
        noResultsText={texts.noResults}
        columns={[
          {
            headerText: texts.documentsOthersTable.name,
            columnName: 'name',
            width: '33%',
            thClassName: 'text-center',
            tdClassName: 'text-center',
          },
          {
            headerText: texts.documentsOthersTable.description,
            columnName: 'description',
            width: '33%',
            thClassName: 'text-center',
            tdClassName: 'text-center',
          },
          {
            headerText: texts.documentsOthersTable.fileName,
            columnName: 'filename',
            width: '33%',
            thClassName: 'text-center',
            tdClassName: 'text-center',
          },
          {
            headerText: texts.documentsOthersTable.action,
            columnName: 'a',
            width: '100px',
            thClassName: 'text-center',
            tdClassName: 'text-center',
            cellFormat({ row }) {
              return (
                <>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => viewDocument(row)}
                    disabled={loading}
                  >
                    <Icon name="eye" color="primary" />
                  </button>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => getUrl(row.id)}
                    disabled={loading}
                  >
                    <Icon name="download" color="primary" />
                  </button>
                  {hasPermissionToDelete && (
                    <button
                      type="button"
                      className="btn"
                      onClick={() => onDelete(row)}
                    >
                      <Icon name="trash" color="primary" />
                    </button>
                  )}
                </>
              );
            },
          },
        ]}
      />
      <DocumentPreviewModal
        isOpen={preview.isOpen}
        url={preview.url}
        title={preview.title}
        onClose={() => setPreview({ isOpen: false, url: null })}
      />
    </>
  );
}
